import { Database } from 'lucide-react'
import { useState } from 'react'

import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { usePermission } from '@/hooks/usePermission'
import { useRecordGroupSummary } from '@/hooks/useRecordGroups'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import { useSubscriptionStore } from '@/stores/subscription.store'

import { RecordGroupBrowser } from './RecordGroupBrowser'
import { RecordGroupStats } from './RecordGroupStats'
import { CreateGroup } from './createGroup'

export function RecordGroups() {
  const { data: summary, isLoading } = useRecordGroupSummary()
  const lockedResourceIds = useSubscriptionStore((state) => state.lockedResourceIds)
  const [mobileTab, setMobileTab] = useState('groups')
  const canCreate = usePermission().can(PERMISSION_KEYS.RECORD_GROUP_CREATE)

  return (
    <div
      className="flex h-full min-h-0 flex-col gap-4"
      aria-label={isLoading ? 'Loading record groups' : undefined}
      aria-busy={isLoading}
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">Record Groups</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Organize and manage your entity types and data forms
          </p>
        </div>
        {canCreate && <CreateGroup />}
      </header>

      {!isLoading && summary?.total === 0 ? (
        <Card className="border border-border bg-background shadow-none">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <div className="mb-4 rounded-2xl bg-muted p-4 text-primary"><Database className="h-10 w-10" /></div>
            <h2 className="text-lg font-semibold text-foreground">No record groups yet</h2>
            <p className="mt-2 max-w-xs text-sm text-muted-foreground">Get started by creating your first record group to organize your data.</p>
            {canCreate && <div className="mt-6"><CreateGroup /></div>}
          </CardContent>
        </Card>
      ) : (
        <div className="min-h-0 flex-1">
          <div className="hidden h-full min-h-0 flex-col gap-4 xl:flex">
            <div>
              <RecordGroupStats summary={summary} isLoading={isLoading} />
            </div>
            <div className="-mr-4 min-h-0 flex-1 overflow-y-auto pr-4 lg:-mr-6 lg:pr-6">
              <RecordGroupBrowser lockedResourceIds={lockedResourceIds} stickyToolbar />
            </div>
          </div>

          <div className="h-full min-h-0 xl:hidden">
            <Tabs value={mobileTab} onValueChange={setMobileTab} className="flex h-full min-h-0 flex-col">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="groups">Record Groups</TabsTrigger>
                <TabsTrigger value="overview">Overview</TabsTrigger>
              </TabsList>
              <TabsContent
                value="groups"
                className="-mr-4 mt-4 min-h-0 flex-1 overflow-y-auto pr-4 lg:-mr-6 lg:pr-6"
              >
                <RecordGroupBrowser lockedResourceIds={lockedResourceIds} stickyToolbar />
              </TabsContent>
              <TabsContent value="overview" className="mt-4 min-h-0 flex-1 overflow-y-auto">
                <RecordGroupStats summary={summary} isLoading={isLoading} />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      )}
    </div>
  )
}
